/**
 * When a chat last moved, in as few characters as the sidebar can spare.
 *
 * The sidebar's job is getting back to a conversation you remember having, and
 * its titles are generated from the question — so a user who asks similar things
 * ends up with nine rows reading "Top DRG Charges by State". The title stops
 * being an identifier at that point and time is the only cue left, which is why
 * it earns a column of its own rather than a tooltip.
 *
 * Precision is spent where it distinguishes: a clock time for today (several
 * chats an hour), a weekday inside the last week, a date beyond that. Always
 * short enough to sit beside a title without stealing its room.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

/**
 * @param {number|string|Date} updatedAt - epoch ms (what sessions carry), or a Date
 * @param {Date} [now] - injected so the boundaries are testable
 * @returns {string} '' when there is no usable timestamp — the caller renders nothing
 */
export const sessionTimeLabel = (updatedAt, now = new Date()) => {
  const then = updatedAt instanceof Date ? updatedAt : new Date(Number(updatedAt));
  if (Number.isNaN(then.getTime()) || !updatedAt) return '';

  const today = startOfDay(now);
  const thatDay = startOfDay(then);

  // Ahead of the clock: a clock skew between server and browser should read as
  // "just now", not as a date in the future.
  if (thatDay > today) return 'now';
  if (thatDay === today) {
    return then.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }
  if (thatDay === today - DAY_MS) return 'Yesterday';
  // Inside the last week a weekday is more recognisable than a date: "Tue" is
  // where you were, "12 Aug" is a lookup.
  if (today - thatDay < 7 * DAY_MS) {
    return then.toLocaleDateString(undefined, { weekday: 'short' });
  }
  if (then.getFullYear() === now.getFullYear()) {
    return then.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  }
  return then.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};
